import { createAsyncThunk } from '@reduxjs/toolkit';
import { TRACKING_MSGS_MAP } from '../helpers';
import { trackDy, trackDySpa } from '../utils';
import { EVENTS_NAMES_MAP } from '../events-name';
import { INTL_CURRENCY } from '@castlery/config';
import { selectOrder, selectOrderLineItems } from '@castlery/modules-order-domain';
import { logger } from '@castlery/observability';

/**
 * @description Track DY Swatch Add to Cart Event
 * @note DY event : Swatch ATC
 * @scenario 1. Triggered when a customer clicks the 'Add to Cart' button on the swatch popup
 */
export const trackDYSwatchAddToCartEvent = createAsyncThunk(
  'tracking/trackDYSwatchAddToCartEvent',
  async (
    payload: { variant: { name: string; sku: string }; swatchRelatedProductSlug?: string },
    { fulfillWithValue }
  ) => {
    if (!payload.variant.name || !payload.variant.sku) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_SWATCH_ATC, {
      'Swatch Name': payload.variant.name,
      'Swatch SKU': payload.variant.sku,
      'Related Product': payload.swatchRelatedProductSlug ?? '',
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

/**
 * @description Track DY Add to Cart Event
 * @note DY event :Add to Cart
 * @scenario 1. Triggered when a customer clicks the 'Add to Cart' button on the PDP page
 */
export const trackDYAddToCartEvent = createAsyncThunk(
  'tracking/trackDYAddToCartEvent',
  async (
    payload: {
      cartLineItems: ReturnType<typeof selectOrderLineItems>;
      variant: {
        sku: string;
        name: string;
        quantity: number;
      };
    },
    { fulfillWithValue }
  ) => {
    if (!payload.cartLineItems || !payload.variant.sku || !payload.variant.name || !payload.variant.quantity) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const cartLineItems = payload.cartLineItems.filter((item) => !item.is_swatch);
      const cart = cartLineItems.map((item) => ({
        productId: item.variant.sku,
        quantity: item.quantity,
        itemPrice: item.variant.price,
      }));

      trackDy(EVENTS_NAMES_MAP.DY_ADD_TO_CART, {
        dyType: 'add-to-cart-v1',
        // value: payload.variant.price * payload.variant.quantity, // todo: add value
        currency: INTL_CURRENCY,
        productId: payload.variant.sku,
        quantity: payload.variant.quantity,
        cart,
      });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (error) {
      logger.error('trackDYAddToCartEvent failed', { error });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description Track DY Add to Wishlist Event
 * @note DY event : Add to Wishlist
 * @scenario 1. Triggered when a customer clicks the 'like' icon button on the PDP page
 * @scenario 2. Triggered when a customer clicks the 'like' icon button on the PLP page
 */
export const trackDYAddToWishlistEvent = createAsyncThunk(
  'tracking/trackDYAddToWishlistEvent',
  async (payload: { variant: { sku: string } }, { fulfillWithValue }) => {
    if (!payload.variant.sku) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_ADD_TO_WISHLIST, {
      dyType: 'add-to-wishlist-v1',
      productId: payload.variant.sku,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Keyword Search
export const trackDYKeywordSearchEvent = createAsyncThunk(
  'tracking/trackDYKeywordSearchEvent',
  async (payload: { keywords: string }, { fulfillWithValue }) => {
    if (!payload.keywords) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_KEYWORD_SEARCH, {
      dyType: 'keyword-search-v1',
      keywords: payload.keywords,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

/**
 * @description Track DY Filter Items Event
 * @note DY event : Filter Items
 * plp filter event
 */
export const trackDYFilterItemsEvent = createAsyncThunk(
  'tracking/trackDYFilterItemsEvent',
  async (payload: { type: string; value: string }, { fulfillWithValue }) => {
    if (!payload.type || !payload.value) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_FILTER_ITEMS, {
      dyType: 'filter-items-v1',
      filterType: payload.type,
      filterStringValue: payload.value,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Swatch Purchase
export const trackDYSwatchPurchaseEvent = createAsyncThunk(
  'tracking/trackDYSwatchPurchaseEvent',
  async (payload: { swatches: { variant: { sku: string } }[] }, { fulfillWithValue }) => {
    if (!payload.swatches.length) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_SWATCH_PURCHASE, {
      'Swatch SKUs': `${payload.swatches.map((swatch) => swatch.variant.sku).join(', ')}`,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Purchase
export const trackDYPurchaseEvent = createAsyncThunk(
  'tracking/trackDYPurchaseEvent',
  async (
    payload: {
      order: ReturnType<typeof selectOrder>;
    },
    { fulfillWithValue, dispatch }
  ) => {
    if (!payload.order) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    const { line_items, item_count } = payload.order;
    const swatches = line_items.filter((item) => item.is_swatch);
    const trackedOrder = {
      ...payload.order,
      line_items: line_items.filter((item) => !item.is_swatch),
      item_count: item_count - swatches.length,
    };

    trackDy(EVENTS_NAMES_MAP.DY_PURCHASE, {
      uniqueTransactionId: trackedOrder.number,
      dyType: 'purchase-v1',
      value: trackedOrder.total,
      currency: trackedOrder.currency,
      cart: trackedOrder.line_items.map((item) => ({
        productId: item.variant.sku,
        quantity: item.quantity,
        itemPrice: item.variant.price,
      })),
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Remove from Cart
export const trackDYRemoveFromCartEvent = createAsyncThunk(
  'tracking/trackDYRemoveFromCartEvent',
  async (payload: { variant: { sku: string } }, { fulfillWithValue }) => {
    if (!payload.variant.sku) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    // todo: implement

    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Newsletter Subscription
export const trackDYNewsletterSubscriptionEvent = createAsyncThunk(
  'tracking/trackDYNewsletterSubscriptionEvent',
  async (payload: { email: string }, { fulfillWithValue }) => {
    const email = payload.email;
    let hashedEmail = '';
    if (typeof window !== 'undefined' && window.DYO) {
      hashedEmail = window.DYO.dyhash.sha256(email.toLowerCase());
    }
    if (hashedEmail) {
      trackDy(EVENTS_NAMES_MAP.DY_NEWSLETTER_SUBSCRIPTION, {
        dyType: 'newsletter-subscription-v1',
        hashedEmail: hashedEmail,
      });
    }

    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Signup
export const trackDYSignupEvent = createAsyncThunk(
  'tracking/trackDYSignupEvent',
  async (payload: { email: string; id: string }, { fulfillWithValue }) => {
    if (payload.email && typeof window !== 'undefined' && window.DYO) {
      trackDy(EVENTS_NAMES_MAP.DY_SIGNUP, {
        dyType: 'signup-v1',
        hashedEmail: window.DYO.dyhash.sha256(payload.email.toLowerCase()),
      });
    } else if (payload.id) {
      trackDy(EVENTS_NAMES_MAP.DY_SIGNUP, {
        dyType: 'signup-v1',
        cuid: payload.id,
      });
    }

    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Login
export const trackDYLoginEvent = createAsyncThunk(
  'tracking/trackDYLoginEvent',
  async (payload: { email: string; id: string }, { fulfillWithValue }) => {
    if (!payload.email && !payload.id) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    if (payload.email && typeof window !== 'undefined' && window.DYO) {
      trackDy(EVENTS_NAMES_MAP.DY_LOGIN, {
        dyType: 'login-v1',
        hashedEmail: window.DYO.dyhash.sha256(payload.email.toLowerCase()),
      });
    } else if (payload.id) {
      trackDy(EVENTS_NAMES_MAP.DY_LOGIN, {
        dyType: 'login-v1',
        cuid: payload.id,
      });
    }
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Promo Code Entered
export const trackDYPromoCodeEnteredEvent = createAsyncThunk(
  'tracking/trackDYPromoCodeEnteredEvent',
  async (payload: { promoCode: string }, { fulfillWithValue }) => {
    if (!payload.promoCode) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackDy(EVENTS_NAMES_MAP.DY_PROMO_CODE_ENTERED, {
      dyType: 'promo-code-entered-v1',
      promoCode: payload.promoCode,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

// Set SPA Context
// export function setSPAContext(context: string, countAsPageView = true) {
//   if (countAsPageView !== false) {
//     window.DY?.recommendationContext = context;
//   }
//   // dy('spa', {
//   //   context,
//   //   countAsPageView,
//   // });
//   trackDySpa();
// }
